import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Pencil, Trash2, X, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";
import ImageField from "@/components/ImageField";

const EMPTY = { title: "", description: "", date: "", price: "", capacity: 0, image: "", published: true };
const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";

export default function WorkshopsTab() {
  const [workshops, setWorkshops] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editFor, setEditFor] = useState(null);
  const [editImage, setEditImage] = useState("");
  const [regsFor, setRegsFor] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [wlOpen, setWlOpen] = useState(false);

  const load = () => {
    api.get("/admin/workshops").then((r) => setWorkshops(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/workshop-waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const removeFromWaitlist = async (id) => {
    await api.delete(`/admin/workshop-waitlist/${id}`);
    setWaitlist((arr) => arr.filter((w) => w.id !== id));
    toast.success("Removed from waitlist");
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/workshops", { ...form, capacity: Number(form.capacity) || 0 });
      setForm(EMPTY);
      load();
      toast.success("Workshop added");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async (e, id) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.put(`/admin/workshops/${id}`, {
        title: fd.get("title"), description: fd.get("description"), date: fd.get("date"),
        price: fd.get("price"), capacity: Number(fd.get("capacity")) || 0, image: editImage,
        published: fd.get("published") === "on",
      });
      setEditFor(null);
      load();
      toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this workshop and its registrations?")) return;
    await api.delete(`/admin/workshops/${id}`);
    load();
  };

  return (
    <div data-testid="admin-workshops">
      <div className="bg-white border border-[#E5E5E2] p-4 mb-8" data-testid="waitlist-section">
        <button onClick={() => setWlOpen(!wlOpen)} data-testid="waitlist-toggle"
          className="w-full flex items-center justify-between text-sm">
          <span className="inline-flex items-center gap-2 font-medium">
            <Mail size={15} /> Workshop Waitlist
            <span className="text-xs text-[#7A7A7A] font-normal">({waitlist.length} {waitlist.length === 1 ? "person" : "people"})</span>
          </span>
          {wlOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
        {wlOpen && (
          <div className="mt-4 border-t border-[#E5E5E2] pt-3" data-testid="waitlist-list">
            {waitlist.length === 0 && <p className="text-xs text-[#7A7A7A]">No one on the waitlist yet. Share your Workshops page to collect interest.</p>}
            {waitlist.map((w) => (
              <div key={w.id} className="flex items-center justify-between py-1.5 border-b border-[#EFEFEC] last:border-0">
                <p className="text-sm">{w.email} <span className="text-xs text-[#B5B5B0]">· {new Date(w.created_at).toLocaleDateString()}</span></p>
                <button onClick={() => removeFromWaitlist(w.id)} data-testid={`waitlist-remove-${w.id}`} className="p-1.5 text-red-500"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={add} className="bg-white border border-[#E5E5E2] p-6 mb-8 grid sm:grid-cols-2 gap-3">
        <p className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">New Workshop</p>
        <input required placeholder="Workshop title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="workshop-add-title" className={inputCls} />
        <input placeholder="Date (e.g. Saturday, Dec 6 — 10am)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
          data-testid="workshop-add-date" className={inputCls} />
        <input placeholder="Price (e.g. $180)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
          data-testid="workshop-add-price" className={inputCls} />
        <input placeholder="Capacity (e.g. 8)" type="number" min="0" value={form.capacity || ""} onChange={(e) => setForm({ ...form, capacity: e.target.value })}
          data-testid="workshop-add-capacity" className={inputCls} />
        <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="workshop-add-description" className="sm:col-span-2 border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A]" />
        <div className="sm:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Workshop photo</p>
          <ImageField value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="workshop-add-image" />
        </div>
        <button type="submit" data-testid="workshop-add-submit"
          className="sm:col-span-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] transition-colors">
          Add Workshop
        </button>
      </form>

      <div className="space-y-3">
        {workshops.map((w) => (
          <div key={w.id} data-testid={`admin-workshop-${w.id}`} className="bg-white border border-[#E5E5E2] p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{w.title} {!w.published && <span className="text-xs text-[#7A7A7A]">(hidden)</span>}</p>
                <p className="text-xs text-[#7A7A7A]">
                  {w.date || "No date"} · {w.price || "No price"} · {w.capacity ? `${(w.registrations || []).length}/${w.capacity} registered` : `${(w.registrations || []).length} registered`}
                </p>
              </div>
              <button onClick={() => setRegsFor(regsFor === w.id ? null : w.id)} data-testid={`workshop-regs-${w.id}`}
                className="p-2 border border-[#E5E5E2] inline-flex items-center gap-1 text-xs">
                {(w.registrations || []).length} {regsFor === w.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              <button onClick={() => { const next = editFor === w.id ? null : w.id; setEditFor(next); if (next) setEditImage(w.image || ""); }} data-testid={`workshop-edit-${w.id}`} className="p-2 border border-[#E5E5E2]">
                {editFor === w.id ? <X size={13} /> : <Pencil size={13} />}
              </button>
              <button onClick={() => remove(w.id)} data-testid={`workshop-delete-${w.id}`} className="p-2 border border-[#E5E5E2] text-red-500"><Trash2 size={13} /></button>
            </div>

            {regsFor === w.id && (
              <div className="mt-4 border-t border-[#E5E5E2] pt-3" data-testid={`workshop-regs-list-${w.id}`}>
                {(w.registrations || []).length === 0 && <p className="text-xs text-[#7A7A7A]">No registrations yet.</p>}
                {(w.registrations || []).map((r) => (
                  <p key={r.id} className="text-sm py-1.5 border-b border-[#EFEFEC] last:border-0">
                    <span className="font-medium">{r.name}</span> — {r.email}{r.phone ? ` · ${r.phone}` : ""}
                    {r.message && <span className="text-[#7A7A7A]"> · {r.message}</span>}
                    {" "}<span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 ${r.payment_status === "paid" ? "bg-green-50 text-green-700" : r.payment_status === "free" ? "bg-gray-100 text-gray-600" : "bg-amber-50 text-amber-700"}`}>
                      {r.payment_status === "paid" ? "Paid" : r.payment_status === "free" ? "Free" : "Awaiting payment"}
                    </span>
                  </p>
                ))}
              </div>
            )}

            {editFor === w.id && (
              <form onSubmit={(e) => save(e, w.id)} className="mt-4 border-t border-[#E5E5E2] pt-4 grid sm:grid-cols-2 gap-3" data-testid={`workshop-edit-form-${w.id}`}>
                <input name="title" defaultValue={w.title} className={inputCls} />
                <input name="date" defaultValue={w.date} placeholder="Date" className={inputCls} />
                <input name="price" defaultValue={w.price} placeholder="Price" className={inputCls} />
                <input name="capacity" type="number" min="0" defaultValue={w.capacity} placeholder="Capacity" className={inputCls} />
                <input name="description" defaultValue={w.description} placeholder="Description" className="sm:col-span-2 border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A]" />
                <div className="sm:col-span-2">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Workshop photo</p>
                  <ImageField value={editImage} onChange={setEditImage} testId={`workshop-image-${w.id}`} />
                </div>
                <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                  <input name="published" type="checkbox" defaultChecked={w.published} className="accent-[#1A1A1A] w-4 h-4" data-testid={`workshop-published-${w.id}`} />
                  Visible on the site
                </label>
                <button type="submit" data-testid={`workshop-save-${w.id}`}
                  className="bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-2.5 hover:bg-[#4A4A4A] transition-colors justify-self-start">
                  Save
                </button>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
